import {
  shouldShowCoverage,
  type HiddenLevel,
} from '../../../../utils/coverage-visibility';
import { ProductTypes } from '../../../../../../../../types';
import { ModifierTypes } from './modifierTypes';

/**
 * The grid's rules for rate modifiers, shared with the agent's snapshot
 * (OMNI-1215). The snapshot used its own `isHidden` check and the rater marks
 * UW modifiers hidden from agents, so Schedule Rating never reached the agent
 * even though the underwriter could type into it.
 */

export interface ModifierViewer {
  readonly productType: string | undefined;
  readonly isPowerUser: boolean;
}

/**
 * Whether the UW grid renders this modifier's row.
 *
 * The Correction Factor lets an underwriter dial a mis-rated P100 Pro quote
 * back to the premium it was originally quoted at, so only power users get it.
 * The rater already withholds it everywhere else: it is dormant outside rater
 * minors 5-7 and carries hiddenLevels FROM_AGENT.
 */
export function canShowModifier(
  modifier: {
    key?: string;
    hiddenLevels?: HiddenLevel[] | null;
    isHidden?: boolean;
  },
  viewer: ModifierViewer
): boolean {
  if (
    viewer.productType === ProductTypes.p100_pro &&
    modifier.key === ModifierTypes.CORRECTION_FACTOR &&
    !viewer.isPowerUser
  ) {
    return false;
  }

  return shouldShowCoverage(modifier, 'UW');
}

/** Modifiers `ModifierInputFactory` gives an input. Any other renders none. */
const MODIFIERS_WITH_INPUT: ReadonlySet<string> = new Set(
  Object.values(ModifierTypes)
);

/**
 * Whether the grid lets the underwriter type into this modifier's value.
 *
 * `isEditable` is not this: on the grid it only drives the include checkbox.
 * The input renders whenever the modifier has rules and a value, and is
 * disabled only by the locks below (and by min equal to max, which is per
 * field and left to the caller).
 */
export function isModifierInputEditable(
  modifier: {
    key?: string;
    rules?: Record<string, unknown> | null;
    value?: unknown;
  },
  productType: string | undefined
): boolean {
  const id = modifier.key ?? '';
  if (!MODIFIERS_WITH_INPUT.has(id)) {
    return false;
  }
  if (!modifier.rules || Object.keys(modifier.rules).length === 0) {
    return false;
  }
  if (!modifier.value) {
    return false;
  }
  // TextField locks this one everywhere.
  if (id === ModifierTypes.RENEWAL_RATING_FACTOR) {
    return false;
  }
  // P100 Pro retired its IRPM; IrpmField renders it read-only.
  return !(productType === ProductTypes.p100_pro && id === ModifierTypes.IRPM);
}
